import React, { useEffect, useRef, useState } from "react";
import JeaInput from "./JeaInput";
import { cn } from "@/lib/utils";

/**
 * Currency input — accepts whole dollars OR decimal amounts.
 *
 * Internal representation: cents (integer). Parent receives cents via `onChange(cents)`.
 *
 * Input rules while typing (focused):
 *   - Allow digits and at most one `.`
 *   - After `.`, allow up to 2 digits
 *   - Cap total digits so we never exceed $99,999,999.99 (cents fit in a JS int)
 *   - Display shows the user's typed intent as `$` + grouped-digits + `.decimals`.
 *     e.g. `25000.5` → focused `$25,000.5`, `25000` → focused `$25,000`.
 *   - On blur we pad to `.XX` for consistent presentation.
 */
const MoneyInput = ({
  valueCents = 0,
  onChange,
  placeholder = "$0.00",
  className,
  testid,
  disabled = false,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const centsRef = useRef(valueCents);
  // `raw` is a numeric-string like "25000" or "25000.5" or "25000.55".
  // It does NOT contain `$` or commas — those are added on display.
  const [raw, setRaw] = useState(() => centsToRaw(valueCents));

  useEffect(() => {
    if (focused) return;
    if (centsRef.current === valueCents) return;
    centsRef.current = valueCents;
    setRaw(centsToRaw(valueCents));
  }, [valueCents, focused]);

  const stripFormatting = (v) => {
    // Remove $, commas, spaces
    return (v || "").replace(/[^\d.]/g, "");
  };

  const normaliseTyped = (v) => {
    // Keep only digits + at most one dot + max 2 decimals + max 10 int digits
    let cleaned = "";
    let sawDot = false;
    let decimals = 0;
    let ints = 0;
    for (const ch of v) {
      if (ch === ".") {
        if (sawDot) continue;
        sawDot = true;
        cleaned += ".";
        continue;
      }
      if (ch >= "0" && ch <= "9") {
        if (sawDot) {
          if (decimals >= 2) continue;
          decimals += 1;
        } else {
          if (ints >= 10) continue;
          ints += 1;
        }
        cleaned += ch;
      }
    }
    return cleaned;
  };

  const rawToCents = (r) => {
    if (!r) return 0;
    if (!/^\d*(\.\d{0,2})?$/.test(r)) return 0;
    const parts = r.split(".");
    const int = parts[0] || "0";
    const dec = (parts[1] || "").padEnd(2, "0").slice(0, 2);
    return parseInt(int, 10) * 100 + parseInt(dec || "0", 10);
  };

  const handleChange = (e) => {
    const stripped = stripFormatting(e.target.value);
    const clean = normaliseTyped(stripped);
    setRaw(clean);
    const cents = rawToCents(clean);
    centsRef.current = cents;
    onChange && onChange(cents);
  };

  const formatFocused = (r) => {
    if (!r) return "";
    const [intPart, decPart] = r.split(".");
    const intFmt = Number(intPart || "0").toLocaleString("en-US");
    return decPart !== undefined ? `$${intFmt}.${decPart}` : `$${intFmt}`;
  };

  const formatBlurred = (r) => {
    if (!r) return "";
    const cents = rawToCents(r);
    return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
  };

  const display = focused ? formatFocused(raw) : formatBlurred(raw);

  return (
    <JeaInput
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={display}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(
        "jea-mono font-semibold text-accent-pink placeholder:text-text-secondary",
        className
      )}
      data-testid={testid}
      {...rest}
    />
  );
};

function centsToRaw(cents) {
  if (!cents || cents <= 0) return "";
  const whole = Math.floor(cents / 100);
  const dec = cents % 100;
  if (dec === 0) return String(whole);
  return `${whole}.${String(dec).padStart(2, "0")}`;
}

export default MoneyInput;
